import type { ComponentPropsWithoutRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@exopengithub/exokit";
import { IconChevronDown } from "@tabler/icons-react";
import { clsx } from "clsx";
import { Input } from "molecules/input/Input";
import {
	Fragment,
	type ReactNode,
	type RefObject,
	useMemo,
	useState,
} from "react";

export type EntityPickerFieldItem = {
	id: string;
	name: string;
};

export type EntityPickerFieldProps<T extends EntityPickerFieldItem> = {
	pillLabel: string;
	pillClassName?: string;
	currentLabel: string;
	items: T[];
	searchPlaceholder: string;
	/** Rendered top-right of the popover header, next to the current value — e.g. a "create new" action. */
	renderHeaderAction?: (helpers: { close: () => void }) => ReactNode;
	interactive?: boolean;
	renderItem: (item: T, helpers: { close: () => void }) => ReactNode;
};

export const EntityPickerField = <T extends EntityPickerFieldItem>({
	pillLabel,
	pillClassName = "bg-[#ccf5d6] text-[#00661a]",
	currentLabel,
	items,
	searchPlaceholder,
	renderHeaderAction,
	renderItem,
	interactive = true,
	...buttonProps
}: EntityPickerFieldProps<T> &
	Omit<ComponentPropsWithoutRef<"button">, "children">) => {
	const [filter, setFilter] = useState("");
	const filteredItems = useMemo(() => {
		const normalizedFilter = filter.toLocaleLowerCase();
		return items.filter((item) =>
			item.name.toLocaleLowerCase().includes(normalizedFilter),
		);
	}, [items, filter]);

	const trigger = (
		<button
			{...buttonProps}
			type="button"
			disabled={buttonProps.disabled || !interactive}
			className={clsx(
				"flex h-9 w-full items-center gap-2 p-2 text-left",
				interactive && "hover:bg-gray-100 aria-expanded:bg-gray-100",
				buttonProps.className,
			)}
		>
			<span
				className={clsx(
					"shrink-0 rounded-full px-1.5 py-0.5 font-medium text-xs",
					pillClassName,
				)}
			>
				{pillLabel}
			</span>
			<div
				className={clsx(
					"grow truncate",
					interactive ? "text-grey-900" : "text-grey-500",
				)}
			>
				{currentLabel}
			</div>
			{interactive && (
				<IconChevronDown
					className="shrink-0 text-primary-800"
					size={20}
					strokeWidth={1.5}
				/>
			)}
		</button>
	);

	if (!interactive) {
		return trigger;
	}

	return (
		<Popover role="menu" withArrow={false}>
			<PopoverTrigger asChild>{trigger}</PopoverTrigger>
			<PopoverContent className="w-96">
				{({ close, initialElementRef }) => {
					return (
						<Popover.ContentContainer>
							<div className="flex items-center gap-2 border-b p-2 pl-4">
								<div className="grow truncate font-medium">{currentLabel}</div>
								{renderHeaderAction?.({ close })}
							</div>
							{items.length > 1 && (
								<div className={clsx(filteredItems.length > 0 && "border-b")}>
									<Input
										borderless
										type="search"
										placeholder={searchPlaceholder}
										ref={
											initialElementRef as RefObject<HTMLInputElement | null>
										}
										value={filter}
										onChange={(e) => {
											setFilter(e.target.value);
										}}
										clearable
									/>
								</div>
							)}
							{filteredItems.length > 0 && (
								<div className="flex max-h-96 flex-col overflow-auto">
									{filteredItems.map((item) => (
										<Fragment key={item.id}>
											{renderItem(item, { close })}
										</Fragment>
									))}
								</div>
							)}
						</Popover.ContentContainer>
					);
				}}
			</PopoverContent>
		</Popover>
	);
};
